<template>
  <div class="app-container">
    <!-- 搜索区域 -->
    <el-form :inline="true" :model="query" class="search-form">
      <el-form-item label="平台">
        <el-select v-model="platformFilter" placeholder="请选择平台" clearable @change="fetchData">
          <el-option v-for="item in platformList" :key="item.id" :label="item.platformName" :value="item.platformCode" />
        </el-select>
      </el-form-item>
      <el-form-item label="店铺">
        <el-select v-model="shopFilter" placeholder="请选择店铺" clearable filterable @change="fetchData">
          <el-option v-for="item in shopList" :key="item.id" :label="item.shopName" :value="item.shopCode" />
        </el-select>
      </el-form-item>
      <el-form-item label="业务类型">
        <el-select v-model="businessTypeFilter" placeholder="请选择" clearable @change="fetchData">
          <el-option label="Order" value="Order" />
          <el-option label="Product" value="Product" />
          <el-option label="Stock" value="Stock" />
          <el-option label="Payment" value="Payment" />
          <el-option label="Logistics" value="Logistics" />
        </el-select>
      </el-form-item>
      <el-form-item label="调用结果">
        <el-select v-model="successFilter" placeholder="请选择" clearable @change="fetchData">
          <el-option label="成功" :value="true" />
          <el-option label="失败" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item label="时间范围">
        <el-date-picker
          v-model="timeRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="YYYY-MM-DD HH:mm:ss"
          @change="fetchData"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="Search" @click="fetchData">查询</el-button>
        <el-button icon="Refresh" @click="resetSearch">重置</el-button>
        <el-button type="danger" icon="Warning" @click="fetchFailedLogs">失败日志</el-button>
      </el-form-item>
    </el-form>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="statistics-row">
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">总调用次数</div>
            <div class="stat-value">{{ statistics.total }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">成功次数</div>
            <div class="stat-value success">{{ statistics.success }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">失败次数</div>
            <div class="stat-value error">{{ statistics.failed }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover">
          <div class="stat-item">
            <div class="stat-label">平均耗时(ms)</div>
            <div class="stat-value">{{ statistics.avgTime }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 表格区域 -->
    <basic-table :query="query" @handler="fetchData">
      <template #platformCode="{ row }">
        <el-tag type="primary" size="small">{{ row.platformCode }}</el-tag>
      </template>
      <template #shopCode="{ row }">
        <el-tag size="small">{{ row.shopCode }}</el-tag>
      </template>
      <template #httpMethod="{ row }">
        <el-tag size="small">{{ row.httpMethod }}</el-tag>
      </template>
      <template #isSuccess="{ row }">
        <el-tag :type="row.isSuccess ? 'success' : 'danger'" size="small">
          {{ row.isSuccess ? '成功' : '失败' }}
        </el-tag>
      </template>
      <template #statusCode="{ row }">
        <el-tag size="small">{{ row.statusCode }}</el-tag>
      </template>
      <template #elapsedTime="{ row }">
        <span :class="getElapsedTimeClass(row.elapsedTime)">{{ row.elapsedTime }}ms</span>
      </template>
      <template #businessType="{ row }">
        <el-tag type="info" size="small">{{ row.businessType }}</el-tag>
      </template>
      <template #toolbar="{ row }">
        <el-button link type="primary" size="small" icon="View" @click="handleView(row)">详情</el-button>
        <el-button link type="danger" size="small" icon="Delete" @click="handleDelete(row)">删除</el-button>
      </template>
    </basic-table>

    <!-- 查看详情对话框 -->
    <el-dialog v-model="viewDialogVisible" title="接口日志详情" width="900px">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="平台编码">{{ viewData.platformCode }}</el-descriptions-item>
        <el-descriptions-item label="店铺编码">{{ viewData.shopCode }}</el-descriptions-item>
        <el-descriptions-item label="API方法名">{{ viewData.apiMethod }}</el-descriptions-item>
        <el-descriptions-item label="HTTP方法">
          <el-tag>{{ viewData.httpMethod }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="请求URL" :span="2">{{ viewData.apiUrl }}</el-descriptions-item>
        <el-descriptions-item label="请求时间">{{ this.$util.fmtDate(viewData.requestTime) }}</el-descriptions-item>
        <el-descriptions-item label="请求IP">{{ viewData.requestIp }}</el-descriptions-item>
        <el-descriptions-item label="响应时间">{{ this.$util.fmtDate(viewData.responseTime) }}</el-descriptions-item>
        <el-descriptions-item label="耗时">
          <span :class="getElapsedTimeClass(viewData.elapsedTime)">{{ viewData.elapsedTime }}ms</span>
        </el-descriptions-item>
        <el-descriptions-item label="HTTP状态码">
          <el-tag>{{ viewData.statusCode }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="是否成功">
          <el-tag :type="viewData.isSuccess ? 'success' : 'danger'">{{ viewData.isSuccess ? '成功' : '失败' }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="错误码">{{ viewData.errorCode }}</el-descriptions-item>
        <el-descriptions-item label="错误信息">{{ viewData.errorMessage }}</el-descriptions-item>
        <el-descriptions-item label="业务类型">{{ viewData.businessType }}</el-descriptions-item>
        <el-descriptions-item label="业务ID">{{ viewData.businessId }}</el-descriptions-item>
        <el-descriptions-item label="追踪ID">{{ viewData.traceId }}</el-descriptions-item>
        <el-descriptions-item label="备注">{{ viewData.remark }}</el-descriptions-item>
      </el-descriptions>
      
      <el-divider content-position="left">请求参数</el-divider>
      <el-input v-model="viewData.requestParams" type="textarea" :rows="8" readonly />
      
      <el-divider content-position="left">响应内容</el-divider>
      <el-input v-model="viewData.responseBody" type="textarea" :rows="10" readonly />
      
      <el-divider content-position="left">创建信息</el-divider>
      <el-descriptions :column="2" border>
        <el-descriptions-item label="创建时间">{{ this.$util.fmtDate(viewData.createDate) }}</el-descriptions-item>
        <el-descriptions-item label="创建人">{{ viewData.createUserName }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>
  </div>
</template>

<script>
import BasicTable from '@/components/basic-table.vue'
import { getEnabledPlatformList, getEnabledShopList, getFailedLogs } from '@/api/platform'

export default {
  name: 'PlatformApiLog',
  components: {
    BasicTable
  },
  data() {
    return {
      query: {
        keyword: '',
        filters: [],
        sidx: 'RequestTime',
        sord: 'descending',
        page: 1,
        rows: 20,
        data: [],
        columns: [
          { type: 'selection', width: '55' },
          { type: 'index', label: '序号', width: '60' },
          { type: 'template', prop: 'platformCode', label: '平台', width: '100' },
          { type: 'template', prop: 'shopCode', label: '店铺', width: '130' },
          { prop: 'apiMethod', label: 'API方法', width: '200', showOverflowTooltip: true },
          { type: 'template', prop: 'httpMethod', label: 'HTTP', width: '80' },
          { prop: 'requestTime', label: '请求时间', width: '160', sortable: true },
          { type: 'template', prop: 'elapsedTime', label: '耗时', width: '100', sortable: true },
          { type: 'template', prop: 'isSuccess', label: '结果', width: '80' },
          { type: 'template', prop: 'statusCode', label: '状态码', width: '90' },
          { prop: 'errorCode', label: '错误码', width: '100' },
          { prop: 'errorMessage', label: '错误信息', minWidth: '180', showOverflowTooltip: true },
          { type: 'template', prop: 'businessType', label: '业务类型', width: '100' },
          { prop: 'businessId', label: '业务ID', width: '150', showOverflowTooltip: true },
          { prop: 'traceId', label: '追踪ID', width: '150', showOverflowTooltip: true },
          { type: 'toolbar', label: '操作', width: '150', fixed: 'right' }
        ]
      },
      platformFilter: '',
      shopFilter: '',
      businessTypeFilter: '',
      successFilter: '',
      timeRange: [],
      platformList: [],
      shopList: [],
      viewDialogVisible: false,
      viewData: {},
      statistics: {
        total: 0,
        success: 0,
        failed: 0,
        avgTime: 0
      }
    }
  },
  mounted() {
    this.$kaung.init(this.$options.name)
    this.loadPlatformList()
    this.loadShopList()
    this.initDefaultTimeRange()
    this.fetchData()
  },
  methods: {
    builder() {
      let { data, records, total, ...params } = { ...this.query }
      
      // 重置并添加筛选条件
      params.filters = []
      if (this.platformFilter) {
        params.filters.push({ name: 'platformCode', value: this.platformFilter, operate: 0 })
      }
      if (this.shopFilter) {
        params.filters.push({ name: 'shopCode', value: this.shopFilter, operate: 0 })
      }
      if (this.businessTypeFilter) {
        params.filters.push({ name: 'businessType', value: this.businessTypeFilter, operate: 0 })
      }
      if (this.successFilter !== '') {
        params.filters.push({ name: 'isSuccess', value: this.successFilter, operate: 0 })
      }
      if (this.timeRange && this.timeRange.length === 2) {
        params.filters.push({ name: 'requestTime', value: this.timeRange[0], operate: 2 }) // >=
        params.filters.push({ name: 'requestTime', value: this.timeRange[1], operate: 4 }) // <=
      }
      return params
    },
    fetchData() {
      this.$kaung.search(this.builder()).then(res => {
        this.query = { ...res.data }
        this.calculateStatistics()
      })
    },
    resetSearch() {
      this.platformFilter = ''
      this.shopFilter = ''
      this.businessTypeFilter = ''
      this.successFilter = ''
      this.timeRange = []
      this.initDefaultTimeRange()
    },
    initDefaultTimeRange() {
      // 默认查询最近7天
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 7)
      this.timeRange = [
        this.$util.fmtDate(start, 'yyyy-MM-dd HH:mm:ss'),
        this.$util.fmtDate(end, 'yyyy-MM-dd HH:mm:ss')
      ]
      this.fetchData()
    },
    fetchFailedLogs() {
      const params = {
        platformCode: this.platformFilter || undefined,
        startTime: this.timeRange && this.timeRange.length === 2 ? this.timeRange[0] : undefined,
        endTime: this.timeRange && this.timeRange.length === 2 ? this.timeRange[1] : undefined
      }
      getFailedLogs(params).then(res => {
        this.query.data = res.data || []
        this.$message.info(`共找到 ${this.query.data.length} 条失败日志`)
        this.calculateStatistics()
      })
    },
    loadPlatformList() {
      getEnabledPlatformList().then(res => {
        this.platformList = res.data || []
      })
    },
    loadShopList() {
      getEnabledShopList().then(res => {
        this.shopList = res.data || []
      })
    },
    handleView(row) {
      this.$kaung.getone(row.id).then(res => {
        this.viewData = { ...res.data }
        // 格式化JSON
        try {
          if (this.viewData.requestParams) {
            this.viewData.requestParams = JSON.stringify(JSON.parse(this.viewData.requestParams), null, 2)
          }
          if (this.viewData.responseBody) {
            this.viewData.responseBody = JSON.stringify(JSON.parse(this.viewData.responseBody), null, 2)
          }
        } catch (e) {
          // 不是JSON格式，保持原样
        }
        this.viewDialogVisible = true
      })
    },
    handleDelete(row) {
      this.$confirm('确定要删除该日志吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$kaung.delete(row.id).then(() => {
          this.$message.success('删除成功')
          this.fetchData()
        })
      })
    },
    calculateStatistics() {
      const data = this.query.data || []
      this.statistics.total = data.length
      this.statistics.success = data.filter(item => item.isSuccess).length
      this.statistics.failed = this.statistics.total - this.statistics.success
      
      if (this.statistics.total > 0) {
        const totalTime = data.reduce((sum, item) => sum + (item.elapsedTime || 0), 0)
        this.statistics.avgTime = Math.round(totalTime / this.statistics.total)
      } else {
        this.statistics.avgTime = 0
      }
    },
    getElapsedTimeClass(time) {
      if (time > 5000) return 'slow-time'
      if (time > 2000) return 'normal-time'
      return 'fast-time'
    }
  }
}
</script>

<style scoped>
.app-container {
  padding: 20px;
}
.search-form {
  background: #fff;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 4px;
}
.statistics-row {
  margin-bottom: 20px;
}
.stat-item {
  text-align: center;
}
.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 8px;
}
.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}
.stat-value.success {
  color: #67c23a;
}
.stat-value.error {
  color: #f56c6c;
}
.fast-time {
  color: #67c23a;
}
.normal-time {
  color: #e6a23c;
}
.slow-time {
  color: #f56c6c;
  font-weight: bold;
}
</style>
